import { SUPPORTING_INFORMATION_CATEGORIES } from "utils/constants";

const REIMBURSEMENT_CATEGORY = SUPPORTING_INFORMATION_CATEGORIES.REIMBURSEMENT;

const EXAMPLE_REGIMES = {
  SOCIAL_SECURITY: "SECURITE_SOCIALE",
  LOCAL: "ALSACE_MOSELLE",
};

const EXAMPLE_KINDS = {
  PERCENTAGE: "percentage",
  FLAT: "flat",
};

const isEngineKind = (kind) =>
  kind === EXAMPLE_KINDS.PERCENTAGE || kind === EXAMPLE_KINDS.FLAT;

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toBenefitOrders = (orders) =>
  (Array.isArray(orders) ? orders : [])
    .map((order) => Number(order))
    .filter((order) => Number.isFinite(order));

const isEngineExample = (row) =>
  Boolean(
    row &&
    !row.deletedDate &&
    row.category === REIMBURSEMENT_CATEGORY &&
    isEngineKind(row.value && row.value.kind),
  );

const toFormSupportingInformation = (rows) =>
  (rows || []).map((row) => {
    const value = row.value || {};
    if (row.category !== REIMBURSEMENT_CATEGORY || !isEngineKind(value.kind)) {
      return {
        id: row.id,
        category: row.category,
        weight: row.weight,
        value,
        persisted: true,
      };
    }
    return {
      id: row.id,
      category: row.category,
      weight: row.weight,
      persisted: true,
      value: {
        ...value,
        act: value.act || value.mainInfo || "",
        benefitOrders: toBenefitOrders(value.benefitOrders),
      },
    };
  });

const createExample = ({ id, regime, weight }) => ({
  id,
  category: REIMBURSEMENT_CATEGORY,
  weight,
  persisted: false,
  value: {
    act: "",
    regime: regime || EXAMPLE_REGIMES.SOCIAL_SECURITY,
    kind: EXAMPLE_KINDS.PERCENTAGE,
    benefitOrders: [],
    actualCost: null,
    reimbursementBase: null,
    socialSecurityRate: null,
    socialSecurityRefund: null,
  },
});

const nextExampleWeight = (rows, regime) =>
  (rows || [])
    .filter((row) => isEngineExample(row) && row.value.regime === regime)
    .reduce((max, row) => Math.max(max, Number(row.weight) || 0), 0) + 1;

const normalizeExampleValue = (value = {}) => {
  const act = String(value.act || value.mainInfo || "").trim();
  const regime = value.regime || EXAMPLE_REGIMES.SOCIAL_SECURITY;
  const kind = isEngineKind(value.kind) ? value.kind : EXAMPLE_KINDS.PERCENTAGE;
  const benefitOrders = toBenefitOrders(value.benefitOrders);
  return {
    ...value,
    act,
    mainInfo: act,
    regime,
    kind,
    benefitOrders,
    actualCost: toNumber(value.actualCost),
    reimbursementBase: toNumber(value.reimbursementBase),
    socialSecurityRate: toNumber(value.socialSecurityRate),
    socialSecurityRefund: toNumber(value.socialSecurityRefund),
    extraDetail: `${regime} · ${kind} · postes ${benefitOrders.join(", ")}`,
  };
};

const supportingInformationPayload = (rows) =>
  (rows || []).flatMap((row) => {
    if (!row || !row.id) return [];
    if (row.deletedDate && !row.persisted) return [];
    const payload = {
      id: row.id,
      category: row.category,
      weight: Number(row.weight) || 1,
      value: isEngineExample(row)
        ? normalizeExampleValue(row.value)
        : row.value,
    };
    if (row.deletedDate) payload.deletedDate = row.deletedDate;
    return [payload];
  });

const activeExampleCount = (rows) =>
  (rows || []).filter(isEngineExample).length;

const benefitOrderOptions = (coverages, selectedOrders = []) => {
  const byOrder = new Map();
  (coverages || []).forEach((coverage) => {
    const category = coverage.coverageCategory
      ? coverage.coverageCategory.label
      : "";
    (coverage.coverageMappings || []).forEach((mapping) => {
      const coverageBenefit = mapping.coverage || {};
      const order = Number(coverageBenefit.order);
      if (!Number.isFinite(order)) return;
      const label = [category, coverageBenefit.type, coverageBenefit.subtype]
        .filter(Boolean)
        .join(" — ");
      const current = byOrder.get(order);
      if (!current) byOrder.set(order, label || String(order));
      else if (label && current.indexOf(label) === -1) {
        byOrder.set(order, `${current} / ${label}`);
      }
    });
  });
  toBenefitOrders(selectedOrders).forEach((order) => {
    if (!byOrder.has(order)) byOrder.set(order, String(order));
  });
  return Array.from(byOrder.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([order, label]) => ({
      value: order,
      label: `${order} — ${label}`,
    }));
};

const exampleError = (row) => {
  if (!isEngineExample(row)) return null;
  const value = row.value || {};
  if (!String(value.act || "").trim()) return "L'acte est requis";
  if (!value.regime) return "Le régime est requis";
  if (!isEngineKind(value.kind)) return "Le type de garantie est requis";
  if (!toBenefitOrders(value.benefitOrders).length) {
    return "Au moins un poste de garantie est requis";
  }
  const amounts = [
    ["Les frais réels sont requis", value.actualCost],
    ["La base de remboursement est requise", value.reimbursementBase],
    ["Le remboursement RO est requis", value.socialSecurityRefund],
  ];
  const missingAmount = amounts.find(
    (entry) => toNumber(entry[1]) == null || toNumber(entry[1]) < 0,
  );
  if (missingAmount) return missingAmount[0];
  const rate = toNumber(value.socialSecurityRate);
  if (rate == null || rate < 0 || rate > 1) {
    return "Le taux RO doit être compris entre 0 et 1";
  }
  return null;
};

export {
  EXAMPLE_KINDS,
  EXAMPLE_REGIMES,
  REIMBURSEMENT_CATEGORY,
  activeExampleCount,
  benefitOrderOptions,
  createExample,
  exampleError,
  isEngineExample,
  nextExampleWeight,
  normalizeExampleValue,
  supportingInformationPayload,
  toFormSupportingInformation,
};
